import { useMemo, useSyncExternalStore } from "react";
import type { LatestReading } from "@spf/contracts";
import type { StreamStatus } from "@spf/api-client";

/**
 * What the notifications bell is counting.
 *
 * The design ships a hardcoded list including "Solar array hit 5.8 kW peak
 * output" and two other congratulations. Those are deleted rather than
 * adapted: fake success notices on a dashboard whose whole job is to say when
 * something has broken teach the reader to ignore the bell.
 *
 * What replaces them is everything the browser can honestly say right now --
 * whether the stream is up, whether the newest reading is inside the
 * instance's own staleness window, and whether the inverter is replaying
 * frames it buffered through an outage. The inverter's own fault and warning
 * registers are decoded in `packages/field-map/src/layouts/v1.ts` but do not
 * reach `Reading` yet, because no captured frame has ever carried a non-zero
 * one to check the decode against. When they do, they become entries here and
 * nothing else changes.
 */
export interface Alert {
  id: string;
  severity: "warning" | "info";
  title: string;
  message: string;
}

interface Snapshot {
  latest: LatestReading | null;
  status: StreamStatus;
}

const EMPTY: Snapshot = { latest: null, status: "connecting" };

let snapshot: Snapshot = EMPTY;
const listeners = new Set<() => void>();

/**
 * Published from `useLiveTelemetry`, so the header shares the one stream the
 * page already holds. Opening a second connection for the bell would spend one
 * of the server's four per-user slots on a badge.
 */
export function publishTelemetry(next: Snapshot): void {
  if (next.latest === snapshot.latest && next.status === snapshot.status) return;
  snapshot = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function read(): Snapshot {
  return snapshot;
}

/** Reset between tests, and when a session ends. */
export function clearTelemetry(): void {
  snapshot = EMPTY;
  for (const listener of listeners) listener();
}

export function alertsFrom({ latest, status }: Snapshot): Alert[] {
  const alerts: Alert[] = [];

  if (status === "offline") {
    alerts.push({
      id: "stream",
      severity: "warning",
      title: "Live connection dropped",
      message:
        "The browser is not receiving readings. Figures on screen are the last ones that arrived.",
    });
  }

  if (status === "crowded") {
    alerts.push({
      id: "crowded",
      severity: "warning",
      title: "Too many live views open",
      message:
        "The server allows a limited number of live connections per person, and this one was refused. Close another tab and retry.",
    });
  }

  if (latest?.stale === true) {
    alerts.push({
      id: "stale",
      severity: "warning",
      title: "Readings are stale",
      message: `Nothing has arrived from the inverter for longer than the ${String(latest.staleAfterSeconds)} seconds this instance allows.`,
    });
  }

  if (latest?.reading?.isBuffered === true) {
    alerts.push({
      id: "buffered",
      severity: "info",
      title: "Replaying buffered frames",
      message:
        "The inverter stored these while it could not reach the network, and is sending them now. They are older than they look.",
    });
  }

  return alerts;
}

export function useAlerts(): Alert[] {
  const current = useSyncExternalStore(subscribe, read, read);
  return useMemo(() => alertsFrom(current), [current]);
}
